import type { TicketJira } from '@vd/types';
import { MenuItem, jiraIssueHref } from '@vd/ui';

/** What the menu needs of a ticket's Jira state: none while it was never sent. */
export type JiraMenuState = Pick<TicketJira, 'state' | 'issueKey' | 'issueUrl'> | null | undefined;

/**
 * A ticket's Jira entries in its three-dots menu, on the board's cards and the
 * ticket page (TOADO-708): the issue once it is in Jira, whoever is looking;
 * for someone who may send from a project that can, a send; and, for a
 * member, an unlink once there is an issue to forget (one deleted in Jira, or
 * the wrong one), whether or not the project can send now: a workspace below
 * the Team plan keeps its links usable (TOADO-713). Nothing while none applies.
 */
export function JiraMenuItem({ jira, canSend, canUnlink, onSend, onUnlink, onDone }: {
  jira: JiraMenuState;
  canSend: boolean;
  canUnlink: boolean;
  onSend: () => void;
  onUnlink: () => void;
  onDone: () => void;
}) {
  const href = jiraIssueHref(jira?.issueUrl);
  // Whenever there is an issue, in any state: one made before an image failed
  // to upload is still there to open, by anyone (Codex review round 29).
  const open = jira && href
    ? <MenuItem onClick={() => { window.open(href, '_blank', 'noopener,noreferrer'); onDone(); }}>
      {`Open ${jira.issueKey ?? 'the issue'} in Jira`}
    </MenuItem>
    : null;
  if (jira?.state === 'pending') return <>{open}{canSend && <MenuItem disabled>Sending to Jira…</MenuItem>}</>;
  return <>
    {open}
    {canSend && jira?.state !== 'exported' && <MenuItem onClick={() => { onSend(); onDone(); }}>
      {jira?.state === 'failed' ? 'Retry sending to Jira' : 'Send to Jira'}
    </MenuItem>}
    {(canSend || canUnlink) && jira?.issueKey && <MenuItem onClick={() => { onUnlink(); onDone(); }}>
      {`Unlink from ${jira.issueKey}`}
    </MenuItem>}
  </>;
}
